import { useEffect, useMemo, useState, useCallback } from 'react';
import * as anchor from '@project-serum/anchor';

import styled from 'styled-components';
import { Container, Snackbar } from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import Alert from '@material-ui/lab/Alert';
import { PublicKey } from '@solana/web3.js';
import { useWallet } from '@solana/wallet-adapter-react';
import { WalletDialogButton } from '@solana/wallet-adapter-material-ui';
import {
  awaitTransactionSignatureConfirmation,
  CandyMachineAccount,
  CANDY_MACHINE_PROGRAM,
  getCandyMachineState,
  mintOneToken,
} from './candy-machine';
import { AlertState } from './utils';
import { Header } from './Header';
import { MintButton } from './MintButton';
import { GatewayProvider } from '@civic/solana-gateway-react';

import ChineseRoof from './assets/images/chineseRoof.png';
import MintLive from './assets/images/mintLive.png';

// import doA from './assets/images/gd1.png';
// import doB from './assets/images/gd2.png';
// import doC from './assets/images/gd3.png';
// import doD from './assets/images/gd4.png';
// import doE from './assets/images/gd5.png';
// import doF from './assets/images/gd6.png';
// import doG from './assets/images/gd7.png';
// import doH from './assets/images/gd8.png';
// import doJ from './assets/images/gd9.png';
// import doK from './assets/images/gd10.png';
// import doL from './assets/images/gd11.png';
// import doM from './assets/images/gd12.png';
// import doN from './assets/images/gd13.png';
// import doO from './assets/images/gd14.png';
// import doP from './assets/images/gd15.png';

import DocGif from './assets/images/docgif.gif';

const ConnectButton = styled(WalletDialogButton)`
  width: 100%;
  height: 60px;
  margin-top: 10px;
  margin-bottom: 5px;
  background: linear-gradient(180deg, #604ae5 0%, #813eee 100%);
  color: white;
  font-size: 16px;
  font-weight: bold;
`;

const MintContainer = styled.div``; // add your owns styles here

export interface HomeProps {
  candyMachineId?: anchor.web3.PublicKey;
  connection: anchor.web3.Connection;
  startDate: number;
  txTimeout: number;
  rpcHost: string;
}

const Home = (props: HomeProps) => {
  const [isUserMinting, setIsUserMinting] = useState(false);
  const [candyMachine, setCandyMachine] = useState<CandyMachineAccount>();
  const [alertState, setAlertState] = useState<AlertState>({
    open: false,
    message: '',
    severity: undefined,
  });

  const rpcUrl = props.rpcHost;
  const wallet = useWallet();

  const anchorWallet = useMemo(() => {
    if (
      !wallet ||
      !wallet.publicKey ||
      !wallet.signAllTransactions ||
      !wallet.signTransaction
    ) {
      return;
    }

    return {
      publicKey: wallet.publicKey,
      signAllTransactions: wallet.signAllTransactions,
      signTransaction: wallet.signTransaction,
    } as anchor.Wallet;
  }, [wallet]);

  const refreshCandyMachineState = useCallback(async () => {
    if (!anchorWallet) {
      return;
    }

    if (props.candyMachineId) {
      try {
        const cndy = await getCandyMachineState(
          anchorWallet,
          props.candyMachineId,
          props.connection,
        );
        setCandyMachine(cndy);
      } catch (e) {
        console.log('There was a problem fetching Candy Machine state');
        console.log(e);
      }
    }
  }, [anchorWallet, props.candyMachineId, props.connection]);

  const onMint = async () => {
    try {
      setIsUserMinting(true);
      document.getElementById('#identity')?.click();
      if (wallet.connected && candyMachine?.program && wallet.publicKey) {
        const mintTxId = (
          await mintOneToken(candyMachine, wallet.publicKey)
        )[0];

        let status: any = { err: true };
        if (mintTxId) {
          status = await awaitTransactionSignatureConfirmation(
            mintTxId,
            props.txTimeout,
            props.connection,
            true,
          );
        }

        if (status && !status.err) {
          setAlertState({
            open: true,
            message: 'Congratulations! Mint succeeded!',
            severity: 'success',
          });
        } else {
          setAlertState({
            open: true,
            message: 'Mint failed! Please try again!',
            severity: 'error',
          });
        }
      }
    } catch (error: any) {
      let message = error.msg || 'Minting failed! Please try again!';
      if (!error.msg) {
        if (!error.message) {
          message = 'Transaction Timeout! Please try again.';
        } else if (error.message.indexOf('0x137')) {
          message = `SOLD OUT!`;
        } else if (error.message.indexOf('0x135')) {
          message = `Insufficient funds to mint. Please fund your wallet.`;
        }
      } else {
        if (error.code === 311) {
          message = `SOLD OUT!`;
          window.location.reload();
        } else if (error.code === 312) {
          message = `Minting period hasn't started yet.`;
        }
      }

      setAlertState({
        open: true,
        message,
        severity: 'error',
      });
    } finally {
      setIsUserMinting(false);
    }
  };

  useEffect(() => {
    refreshCandyMachineState();
  }, [
    anchorWallet,
    props.candyMachineId,
    props.connection,
    refreshCandyMachineState,
  ]);

  return (
    <>
    
    <Container>
      <Container style={{display: 'flex', width: '100%', justifyContent: 'center', flexDirection: 'column', alignItems: 'center' }}>
        <img src={ChineseRoof} id={'roof'} alt={'roof'} /> 
      </Container>
      <Container style={{display: 'flex', width: '100%', justifyContent: 'center', flexDirection: 'column', alignItems: 'center' }}>
        <img src={MintLive} id={'mintlive'} alt={'mint is live'} /> 
      </Container>
      <Container maxWidth="xs" style={{ position: 'relative', marginTop: 25 }}>
        
        <Paper
          style={{ padding: 24, backgroundColor: '#151A1F', borderRadius: 6 }}
        >
          {!wallet.connected ? (
            <ConnectButton>Connect Wallet</ConnectButton>
          ) : (
            <>
              <Header candyMachine={candyMachine} />
              <MintContainer>
                {candyMachine?.state.isActive &&
                candyMachine?.state.gatekeeper &&
                wallet.publicKey &&
                wallet.signTransaction ? (
                  <GatewayProvider
                    wallet={{
                      publicKey:
                        wallet.publicKey ||
                        new PublicKey(CANDY_MACHINE_PROGRAM),
                      //@ts-ignore
                      signTransaction: wallet.signTransaction,
                    }}
                    gatekeeperNetwork={
                      candyMachine?.state?.gatekeeper?.gatekeeperNetwork
                    }
                    clusterUrl={rpcUrl}
                    options={{ autoShowModal: false }}
                  >
                    <MintButton
                      candyMachine={candyMachine}
                      isMinting={isUserMinting}
                      onMint={onMint}
                    />
                  </GatewayProvider>
                ) : (
                  <MintButton
                    candyMachine={candyMachine}
                    isMinting={isUserMinting}
                    onMint={onMint}
                  />
                )}
              </MintContainer>
            </>
          )}
        </Paper>
      </Container>
      <Container style={{display: 'flex', width: '100%', paddingTop: 25, justifyContent: 'center', flexDirection: 'column', alignItems: 'center' }}> 
        <img src={DocGif} id={'docgif'} alt={"dragon oppa gif"} />
      </Container>
      <Snackbar
        open={alertState.open}
        autoHideDuration={6000}
        onClose={() => setAlertState({ ...alertState, open: false })}
      >
        <Alert
          onClose={() => setAlertState({ ...alertState, open: false })}
          severity={alertState.severity}
        >
          {alertState.message}
        </Alert>
      </Snackbar>
    </Container>
    
    </>
  );
};

// export const Slider = () => {
//   return (
//     <div className="slider">
//       <div className="slide-track">
//         <div className="slide">
//           <img width={280} height={280} src={doA} />
//         </div>
//         <div className="slide">
//           <img width={280} height={280} src={doB} />
//         </div>
//         <div className="slide">
//           <img width={280} height={280} src={doC} />
//         </div>
//         <div className="slide">
//           <img width={280} height={280} src={doD} />
//         </div>
//         <div className="slide">
//           <img width={280} height={280} src={doE} />
//         </div>
//         <div className="slide">
//           <img width={280} height={280} src={doF} />
//         </div>
//         <div className="slide">
//           <img width={280} height={280} src={doG} />
//         </div>
//         <div className="slide">
//           <img width={280} height={280} src={doH} />
//         </div>
//         <div className="slide">
//           <img width={280} height={280} src={doJ} />
//         </div>
//         <div className="slide">
//           <img width={280} height={280} src={doK} />
//         </div>
//         <div className="slide">
//           <img width={280} height={280} src={doL} />
//         </div>
//         <div className="slide">
//           <img width={280} height={280} src={doM} />
//         </div>
//         <div className="slide">
//           <img width={280} height={280} src={doN} />
//         </div>
//         <div className="slide">
//          <img width={280} height={280} src={doO} />
//         </div>
//         <div className="slide">
//           <img width={280} height={280} src={doP} />
//         </div>
//       </div>
//     </div>
//   );
// };
export default Home;
